import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useForm } from 'react-hook-form';
import { css } from '@emotion/react';
import { useMutation } from 'react-query';
import { isNil } from 'lodash-es';

import type { Organization } from '@hotjar/organization';
import {
  tokens,
  TextInput,
  Field,
  Stack,
  Button,
  Text,
  useNotify,
  Link,
  Tooltip,
} from '@hotjar/hotsauce';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useHelpHotjarUrl, useTranslation } from '@hotjar/translations';
import { ModalDeleteRecipientEmail } from './ModalDeleteRecipientEmail';
import type { DeleteRecipientEmailRequest, CreateUpdateRecipientEmailRequest } from './api/api';
import {
  updateRecipientEmail,
  getRecipientEmail,
  deleteRecipientEmail,
  createRecipientEmail,
} from './api/api';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x2};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x3};
`;

interface FormResult {
  recipientEmail: string;
}

interface RequestByApiSectionProps {
  organization: Organization;
  hasLookupCreatePermission: boolean;
  hasLookupReadPermission: boolean;
}

const RECIPIENT_EMAIL = 'recipientEmail';

export const RequestByApiSection = ({
  organization,
  hasLookupCreatePermission,
  hasLookupReadPermission,
}: RequestByApiSectionProps): JSX.Element => {
  const { t } = useTranslation('common');
  const { showToast } = useNotify();
  const [isRecipientEmailExist, setIsRecipientEmailExist] = useState(false);
  const { register, handleSubmit, errors, getValues, reset } = useForm<FormResult>();
  const resetForm = useCallback(async () => {
    const recipientEmail = hasLookupReadPermission
      ? await getRecipientEmail({
          organizationId: organization.id,
          accountId: organization.accountId,
        })
      : undefined;
    setIsRecipientEmailExist(!isNil(recipientEmail));
    reset({
      recipientEmail,
    });
  }, [hasLookupReadPermission, organization.accountId, organization.id, reset]);
  const deleteMutation = useMutation(
    (request: DeleteRecipientEmailRequest) => deleteRecipientEmail(request),
    {
      onSuccess: () => {
        showToast({
          text: t('visitor-lookup.request-by-api-section.recipient-email-removed'),
          kind: 'success',
        });
        resetForm();
      },
      onError: () => {
        showToast({ text: t('error.default-message'), kind: 'danger' });
      },
    },
  );
  const saveMutation = useMutation(
    (request: CreateUpdateRecipientEmailRequest) => {
      return isRecipientEmailExist ? updateRecipientEmail(request) : createRecipientEmail(request);
    },
    {
      onSuccess: () => {
        showToast({
          text: t('visitor-lookup.request-by-api-section.recipient-email-saved'),
          kind: 'success',
        });
        resetForm();
      },
      onError: () => {
        showToast({ text: t('error.default-message'), kind: 'danger' });
      },
    },
  );
  const helpHotjarUrl = useHelpHotjarUrl();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    resetForm();
  }, [organization, resetForm]);

  const onSubmitSave = () => {
    const values = getValues();
    saveMutation.mutate({
      organizationId: organization.id,
      accountId: organization.accountId,
      recipientEmail: values.recipientEmail,
    });
  };

  const onDeleteClicked = () => {
    setIsModalOpen(true);
  };

  const submitDelete = async () => {
    deleteMutation.mutate({ organizationId: organization.id, accountId: organization.accountId });
  };

  const accessRequiredLabel = t('visitor-lookup.request-by-api-section.admin-access-required');

  return (
    <ContentWrapper>
      <Text as="h2" textType="header5">
        {t('visitor-lookup.request-by-api-section.title')}
      </Text>
      <Text as="p" textType="body2">
        {t('visitor-lookup.request-by-api-section.description')}&nbsp;
        <Link kind="inline" as="a" isExternal href={`${helpHotjarUrl}/articles/12244109929751`}>
          {t('visitor-lookup.request-by-api-section.description-link')}
        </Link>
      </Text>

      <StyledForm
        name="recipientEmailForm"
        id="recipientEmailForm"
        onSubmit={handleSubmit(onSubmitSave)}
      >
        <Stack preset="form">
          <Field
            inputId={RECIPIENT_EMAIL}
            labelText={t(
              'visitor-lookup.request-by-api-section.recipient-email-label',
            ).toUpperCase()}
            helperText={t('visitor-lookup.request-by-api-section.recipient-email-helper')}
            isInvalid={!!errors.recipientEmail}
            invalidText={errors.recipientEmail?.message}
            isRequired
            css={css`
              width: 100%;
            `}
          >
            <Tooltip text={accessRequiredLabel} isDisabled={hasLookupCreatePermission}>
              <div>
                <TextInput
                  id={RECIPIENT_EMAIL}
                  name={RECIPIENT_EMAIL}
                  placeholder={t(
                    'visitor-lookup.request-by-api-section.recipient-email-placeholder',
                  )}
                  isInvalid={!!errors.recipientEmail}
                  isDisabled={!hasLookupCreatePermission}
                  ref={
                    register &&
                    register({
                      required: t(
                        'visitor-lookup.request-by-api-section.recipient-email-enter-valid-address',
                      ),
                      validate: (value: string) =>
                        validateEmail(value) ||
                        t(
                          'visitor-lookup.request-by-api-section.recipient-email-enter-valid-address',
                        ),
                    })
                  }
                  autoComplete="off"
                />
              </div>
            </Tooltip>
          </Field>
        </Stack>
        <ButtonsWrapper>
          <Tooltip text={accessRequiredLabel} isDisabled={hasLookupCreatePermission}>
            <div>
              <Button
                id="submit_btn"
                kind="primary"
                isDisabled={!hasLookupCreatePermission}
                type="submit"
              >
                {t('visitor-lookup.request-by-api-section.save')}
              </Button>
            </div>
          </Tooltip>
          {isRecipientEmailExist && (
            <Tooltip text={accessRequiredLabel} isDisabled={hasLookupCreatePermission}>
              <div>
                <Button
                  id="delete_btn"
                  kind="danger_secondary"
                  isDisabled={!hasLookupCreatePermission}
                  type="button"
                  onClick={onDeleteClicked}
                >
                  {t('visitor-lookup.request-by-api-section.delete')}
                </Button>
              </div>
            </Tooltip>
          )}
        </ButtonsWrapper>
      </StyledForm>
      <ModalDeleteRecipientEmail
        setOpen={setIsModalOpen}
        isOpen={isModalOpen}
        onDelete={submitDelete}
      />
    </ContentWrapper>
  );
};
